import { apiService } from './apiService';
import { DiaryItem } from '../models/diary';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface Ingredient {
  name: string;
  quantity: number;
  calories: number;
  carbohydrates: number;
  proteins: number;
  fats: number;
  water: number | null;
  fiber: number;
}

export interface Meal {
  id: number;
  name: string;
  calories: number;
  ingredients: Ingredient[];
}

export interface ActivityType {
  id: number;
  name: string;
  description: string;
}

export interface Activity {
  id: number;
  name: string;
  activityType: ActivityType | string;
  details: string;
  activityLevel: string;
  expectedDurationMin: number;
}

export interface SupplementCatalog {
  id: number;
  name: string;
  description: string;
  purchaseUrl: string;
  created_at: string;
  updated_at: string;
}

export interface Supplement {
  id: number;
  description: string;
  supplementCatalog: SupplementCatalog;
}

export interface MealTargetAPI {
  id: number;
  calories: number;
  protein: string;
  carbs: string;
  fat: string;
  fiber: string;
  water: string;
}

export interface DiaryItemAPI {
  id: number;
  name: string;
  type: 'MealItem' | 'FitnessItem' | 'SupplementItem';
  subtype: string;
  meals: Meal[];
  activities?: Activity[];
  supplements?: Supplement[];
  order: number;
  date: string;
  dayOfWeek: string;
  timeOfDay: string;
  totalCalories: number;
  hidden?: boolean;
  mealTarget?: MealTargetAPI;
  isOriginal: boolean;
  effectiveDate: string | null;
  cancellationDate: string | null;
  replacedById: number | null;
  originalItemId: number | null;
}

export interface DiaryItemsResponse {
  count: number;
  perpage: number;
  actualPage: number;
  totalPages: number;
  results: DiaryItemAPI[];
}

// Helper function to convert API response to DiaryItem format
const convertApiToDiaryItem = (apiItem: DiaryItemAPI): DiaryItem => {
  return {
    id: apiItem.id,
    type: apiItem.type,
    subtype: apiItem.subtype as any, // Type assertion for subtype compatibility
    activities: apiItem.activities || [],
    name: apiItem.name,
    dayOfWeek: apiItem.dayOfWeek,
    meals: apiItem.meals,
    order: apiItem.order,
    timeOfDay: apiItem.timeOfDay,
    totalCalories: apiItem.totalCalories,
    supplements: apiItem.supplements || [],
    hidden: apiItem.hidden,
    mealTarget: apiItem.mealTarget,
    // API specific fields
    date: apiItem.date,
    isOriginal: apiItem.isOriginal,
    effectiveDate: apiItem.effectiveDate,
    cancellationDate: apiItem.cancellationDate,
    replacedById: apiItem.replacedById,
    originalItemId: apiItem.originalItemId,
  };
};

// API interfaces for existing items
export interface ExistingActivity {
  id: number;
  name: string;
  activityLevel: string;
  activityType: ActivityType;
  details: string;
  expectedDurationMin: number;
}

export interface ExistingSupplement {
  id: number;
  description: string;
  supplementCatalog: {
    id: number;
    name: string;
    description: string;
    purchaseUrl: string;
    created_at: string;
    updated_at: string;
  };
}

export interface ActivitiesResponse {
  message: string;
  data: ExistingActivity[];
  count: number;
}

export interface SupplementsResponse {
  message: string;
  data: ExistingSupplement[];
  count: number;
}

export interface SupplementCatalogResponse {
  count: number;
  perpage: number;
  actual_page: number;
  total_pages: number;
  results: SupplementCatalog[];
}

export const diaryService = {
  // Fetch existing activities from database for a specific user
  getExistingActivities: async (userId: string | number): Promise<ExistingActivity[]> => {
    const url = `${API_BASE_URL}/clinic/activities/?user_id=${userId}`;
    try {
      const response: ActivitiesResponse = await apiService.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching existing activities:', error);
      throw error;
    }
  },

  // Fetch existing supplements from database for a specific user
  getExistingSupplements: async (userId: string | number): Promise<ExistingSupplement[]> => {
    const url = `${API_BASE_URL}/clinic/patient/supplements/${userId}/`;
    try {
      const response: ExistingSupplement[] = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching existing supplements:', error);
      throw error;
    }
  },

  // Fetch supplement catalog
  getSupplementCatalog: async (page = 1, perpage = 100): Promise<SupplementCatalog[]> => {
    const url = `${API_BASE_URL}/clinic/supplement-catalog/?page=${page}&perpage=${perpage}`;
    try {
      const response: SupplementCatalogResponse = await apiService.get(url);
      return response.results;
    } catch (error) {
      console.error('Error fetching supplement catalog:', error);
      throw error;
    }
  },

  // Fetch activity types
  getActivityTypes: async (): Promise<ActivityType[]> => {
    const url = `${API_BASE_URL}/activity-types/`;
    try {
      const response: ActivityType[] = await apiService.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching activity types:', error);
      throw error;
    }
  },
  createDiaryItem: async (patientId: string | number, newItem: Partial<DiaryItem>): Promise<DiaryItem> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/diary-items/create/`;
    
    // Prepare the API data according to the specification
    const apiData: any = {
      name: newItem.name,
      type: newItem.type,
      dayOfWeek: newItem.dayOfWeek,
      timeOfDay: newItem.timeOfDay,
      subtype: newItem.subtype,
      order: newItem.order || 1,
      hidden: newItem.hidden || false
    };

    // Add activities if provided
    if (newItem.activities && newItem.activities.length > 0) {
      apiData.activities = newItem.activities.map(activity => {
        const activityData: any = {
          name: activity.name,
          activityLevel: activity.activityLevel,
          expectedDurationMin: activity.expectedDurationMin
        };
        
        // Include ID for existing activities
        if (activity.id) {
          activityData.id = activity.id;
        } else if (activity.activityTypeId) {
          // For new activities, send the activity type ID (camelCase as per API spec)
          activityData.activityType = activity.activityTypeId;
        }
        
        // Include details if provided
        if (activity.details) {
          activityData.details = activity.details;
        }
        
        return activityData;
      });
    }

    // Add supplements if provided
    if (newItem.supplements && newItem.supplements.length > 0) {
      apiData.supplements = newItem.supplements.map(supplement => {
        const supplementData: any = {
          description: supplement.description
        };
        
        // Include ID for existing supplements
        if (supplement.id) {
          supplementData.id = supplement.id;
        } else if (supplement.supplementCatalog) {
          // For new supplements, send the catalog ID (camelCase)
          supplementData.supplementCatalogId = supplement.supplementCatalog.id;
        }
        
        return supplementData;
      });
    }

    try {
      const response = await apiService.post(url, apiData);
      
      // Convert the response to DiaryItem format
      const createdData = response.data || response;
      return convertApiToDiaryItem(createdData);
    } catch (error) {
      console.error(`Error creating diary item for patient ${patientId}:`, error);
      throw error;
    }
  },

  getDiaryItemsByUserId: async (userId: string | number, page = 1, perpage = 10): Promise<DiaryItemsResponse> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/diary-items?page=${page}&perpage=${perpage}`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching diary items for user ${userId}:`, error);
      throw error;
    }
  },

  getAllDiaryItemsByUserId: async (userId: string | number): Promise<DiaryItem[]> => {
    try {
      // First, get the first page to know total pages
      const firstPage = await diaryService.getDiaryItemsByUserId(userId, 1, 100);
      
      // If there's only one page, return the results
      if (firstPage.totalPages <= 1) {
        return firstPage.results.map(convertApiToDiaryItem);
      }

      // If there are multiple pages, fetch all pages
      const allPages = await Promise.all(
        Array.from({ length: firstPage.totalPages }, (_, i) => 
          diaryService.getDiaryItemsByUserId(userId, i + 1, 100)
        )
      );

      // Combine all results and convert to DiaryItem format
      return allPages.flatMap(page => page.results).map(convertApiToDiaryItem);
    } catch (error) {
      console.error(`Error fetching all diary items for user ${userId}:`, error);
      throw error;
    }
  },

  updateDiaryItem: async (patientId: string | number, diaryItemId: string | number, fullItem: DiaryItem): Promise<DiaryItem> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/diary-items/${diaryItemId}/`;
    
    // Convert camelCase to snake_case for API - only send the editable fields
    const apiData: any = {
      name: fullItem.name,
      order: fullItem.order,
      hidden: fullItem.hidden,
      subtype: fullItem.subtype
    };
    
    if (fullItem.dayOfWeek !== undefined) apiData.day_of_week = fullItem.dayOfWeek;
    if (fullItem.timeOfDay !== undefined) apiData.time_of_day = fullItem.timeOfDay;
    
    // Transform activities if provided
    if (fullItem.activities !== undefined) {
      apiData.activities = fullItem.activities.map(activity => {
        const activityData: any = {
          name: activity.name,
          activityLevel: activity.activityLevel,
          expectedDurationMin: activity.expectedDurationMin
        };
        
        // Include ID for existing activities
        if (activity.id) {
          activityData.id = activity.id;
        } else if (activity.activityTypeId) {
          // For new activities, send the activity type ID (camelCase as per API spec)
          activityData.activityType = activity.activityTypeId;
        }
        
        // Include details if provided
        if (activity.details) {
          activityData.details = activity.details;
        }
        
        return activityData;
      });
    }
    
    // Transform supplements if provided
    if (fullItem.supplements !== undefined) {
      apiData.supplements = fullItem.supplements.map(supplement => {
        const supplementData: any = {
          description: supplement.description
        };
        
        // Include ID for existing supplements
        if (supplement.id) {
          supplementData.id = supplement.id;
        } else if (supplement.supplementCatalog) {
          // For new supplements, send the catalog ID (camelCase)
          supplementData.supplementCatalogId = supplement.supplementCatalog.id;
        }
        
        return supplementData;
      });
    }
    
    if (fullItem.mealTarget !== undefined) apiData.meal_target = fullItem.mealTarget;

    try {
      const response = await apiService.put(url, apiData);
      
      // Handle the nested response structure { data: {...}, message: "..." }
      const updatedData = response.data || response;
      
      // Create the updated item by merging the original item with the API response
      const updatedItem: DiaryItem = {
        ...fullItem, // Keep all original fields
        // Override with updated fields from API response
        name: updatedData.name,
        dayOfWeek: updatedData.dayOfWeek || updatedData.day_of_week,
        timeOfDay: updatedData.timeOfDay || updatedData.time_of_day,
        order: updatedData.order,
        hidden: updatedData.hidden,
        subtype: updatedData.subtype,
        activities: updatedData.activities || fullItem.activities,
        supplements: updatedData.supplements || fullItem.supplements,
        mealTarget: updatedData.mealTarget || updatedData.meal_target || fullItem.mealTarget
      };
      
      return updatedItem;
    } catch (error) {
      console.error(`Error updating diary item ${diaryItemId} for patient ${patientId}:`, error);
      throw error;
    }
  }
};